import { Controller } from '@hotwired/stimulus';

type EventListenerData = {
  receiver: EventTarget;
  event: string;
  callback: EventListenerOrEventListenerObject;
};

type ScrollCallback = (this: Window, event: Event) => unknown;
type ScrollCallbackOptions = boolean | AddEventListenerOptions | undefined;
type ScrollCallbackData = Array<[ScrollCallback, ScrollCallbackOptions]>;

type Feature =
  | 'hover'
  | 'any-hover'
  | 'pointer:none'
  | 'pointer:coarse'
  | 'pointer:fine'
  | 'theme:light'
  | 'theme:dark'
  | 'motion:default'
  | 'motion:reduce';

class ApplicationController<
  ElementType extends Element = Element,
> extends Controller<ElementType> {
  private __eventsListeners: EventListenerData[] = [];
  private __timeouts: (number | NodeJS.Timeout)[] = [];
  private __idleCallbacks: number[] = [];
  private __scrollCallbacks: ScrollCallbackData = [];

  disconnect() {
    this.clearAllTimeouts();
    this.clearAllIdleCallbacks();
  }

  bind(
    receiver: EventTarget,
    events: string,
    callback: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions | undefined,
  ) {
    const listeners = events.split(' ').map((event) => {
      const data = { receiver, event, callback };

      receiver.addEventListener(event, callback, options);
      this.__eventsListeners.push(data);

      return data;
    });

    if (listeners.length === 1) {
      return listeners[0];
    }

    return listeners;
  }

  unbind(...events: string[]) {
    const matchingEvents = this.__eventsListeners.filter(({ event }) =>
      events.includes(event),
    );

    const rest = this.__eventsListeners.filter(
      ({ event }) => !events.includes(event),
    );

    matchingEvents.forEach(({ receiver, event, callback }) => {
      receiver.removeEventListener(event, callback);
    });

    this.__eventsListeners = rest;
  }

  /**
   * Unbinds all given listeners.
   *
   * @param {Object[]} listeners
   */
  unbindListeners(...listeners: EventListenerData[]) {
    listeners.forEach((listener) => {
      const { receiver, event, callback } = listener;
      const index = this.__eventsListeners.indexOf(listener);

      receiver.removeEventListener(event, callback);
      this.__eventsListeners.splice(index, 1);
    });
  }

  /**
   * Executes a callback function after some delay.\
   * Same as window.setTimeout, but timeouts are cancelled automatically on disconnect.
   * - Don't forget to call `super.disconnect()` if you override the disconnect hook.
   *
   * @param {function} callback
   * @param {number} [delay=0]
   */
  later(callback: () => void, delay: number = 0) {
    const id = setTimeout(callback, delay);

    this.__timeouts.push(id);

    return id;
  }

  /**
   * Clears all registered timeouts without waiting for controller disconnect.
   */
  clearAllTimeouts() {
    this.__timeouts.forEach((id) => clearTimeout(id));
    this.__timeouts = [];
  }

  whenIdle(callback: IdleRequestCallback, options?: IdleRequestOptions) {
    this.__idleCallbacks.push(requestIdleCallback(callback, options));
  }

  clearAllIdleCallbacks() {
    this.__idleCallbacks.forEach((id) => cancelIdleCallback(id));
    this.__idleCallbacks = [];
  }

  onScroll(callback: ScrollCallback, options?: ScrollCallbackOptions) {
    window.addEventListener('scroll', callback, options);
    this.__scrollCallbacks.push([callback, options]);
  }

  clearAllScrollCallbacks() {
    this.__scrollCallbacks.forEach(([callback, options]) => {
      window.removeEventListener('scroll', callback, options);
    });

    this.__scrollCallbacks = [];
  }

  hasFeature(feature: Feature): boolean {
    switch (feature) {
      case 'hover': {
        return window.matchMedia('(hover: hover)').matches;
      }

      case 'any-hover': {
        return window.matchMedia('(any-hover: hover)').matches;
      }

      case 'pointer:none': {
        return window.matchMedia('(pointer: none)').matches;
      }

      case 'pointer:coarse': {
        return window.matchMedia('(pointer: coarse)').matches;
      }

      case 'pointer:fine': {
        return window.matchMedia('(pointer: fine)').matches;
      }

      case 'theme:light': {
        return window.matchMedia('(prefers-color-scheme: light)').matches;
      }

      case 'theme:dark': {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }

      case 'motion:default': {
        return window.matchMedia('(prefers-reduced-motion: no-preference)')
          .matches;
      }

      case 'motion:reduce': {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  }

  get isMobile() {
    return window.innerWidth < 1024;
  }
}

export default ApplicationController;
